import { apiErrorSchema } from '@manaro/contracts'
import type { ZodType } from 'zod'

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  schema?: ZodType<T>
  signal?: AbortSignal
}

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

export async function apiRequest<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, signal } = options

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      signal,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new ApiError(0, 'network_error', 'Нет связи с сервером админки. Проверьте подключение.')
  }

  if (response.status === 204) return undefined as T

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload)
    throw new ApiError(
      response.status,
      parsed.success ? parsed.data.error.code : 'unknown',
      parsed.success ? parsed.data.error.message : 'Сервер ответил ошибкой. Попробуйте ещё раз.',
    )
  }

  if (!schema) return payload as T

  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    throw new ApiError(500, 'contract_mismatch', 'Сервер вернул неожиданные данные — обновите страницу.')
  }
  return parsed.data
}
